"use client";

import { type FormEvent, useEffect, useRef, useState } from "react";

import { CrossBorderPanel } from "@/components/cross-border-panel";
import { ManualOrderCreate } from "@/components/manual-order-create";
import { loadOrder, searchOrders } from "@/lib/order-api";
import { defaultDemoOrderDetail, demoOrderSearch } from "@/lib/order-demo-data";
import type { ManualOrderCreateData, OrderDetail, OrderSearchData, OrderStatus } from "@/lib/order-types";

const statusLabels: Record<string, string> = {
    PENDING_PAYMENT: "待支付",
    PAID: "已支付",
    FULFILLING: "履约中",
    PARTIALLY_SHIPPED: "部分发货",
    SHIPPED: "已发货",
    COMPLETED: "已完成",
    CANCEL_PENDING: "取消处理中",
    CANCELLED: "已取消",
    CLOSED: "已关闭",
    UNPAID: "未支付",
    UNFULFILLED: "待履约",
    PARTIALLY_FULFILLED: "部分履约",
    FULFILLED: "已履约",
};

export function orderStatusLabel(status: string): string {
    return statusLabels[status] ?? "未知状态";
}

export function formatMoney(amountMinor: number, currency: string): string {
    return new Intl.NumberFormat("zh-CN", { style: "currency", currency }).format(amountMinor / 100);
}

function formatTime(value: string): string {
    return new Intl.DateTimeFormat("zh-CN", {
        timeZone: "Asia/Shanghai",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    }).format(new Date(value));
}

interface OrdersViewProps {
    csrfToken?: string | null;
    roles?: string[];
}

export function OrdersView({ csrfToken = null, roles = [] }: OrdersViewProps) {
    const [result, setResult] = useState<OrderSearchData>(demoOrderSearch);
    const [detail, setDetail] = useState<OrderDetail>(defaultDemoOrderDetail);
    const [source, setSource] = useState<"live" | "demo">("demo");
    const [keyword, setKeyword] = useState("");
    const [status, setStatus] = useState<OrderStatus | "">("");
    const [isLoading, setIsLoading] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [createdOrderNumber, setCreatedOrderNumber] = useState<string | null>(null);
    const selectedOrderId = useRef<string | null>(null);

    useEffect(() => {
        void searchOrders().then((response) => {
            setResult(response.data);
            setSource(response.source);
            const first =
                response.data.items.find((order) => order.channel === "US_DTC" || order.currency === "USD") ?? response.data.items[0];
            if (first && selectedOrderId.current === null) void openOrder(first.id);
        });
    }, []);

    async function openOrder(orderId: string) {
        selectedOrderId.current = orderId;
        const response = await loadOrder(orderId);
        setDetail(response.data);
        setSource(response.source);
    }

    async function submitSearch(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setIsLoading(true);
        const response = await searchOrders({ keyword: keyword.trim(), statuses: status ? [status] : [] });
        setResult(response.data);
        setSource(response.source);
        setIsLoading(false);
    }

    function handleCreated(created: ManualOrderCreateData) {
        selectedOrderId.current = created.order.id;
        setDetail(created.order);
        setSource("live");
        setShowCreate(false);
        setCreatedOrderNumber(created.order.order_number);
        void searchOrders().then((response) => {
            setResult(response.data);
            setSource(response.source);
        });
    }

    const canCreate = Boolean(csrfToken && roles.some((role) => role === "ADMIN" || role === "ORDER_OPERATOR"));

    return (
        <section className="workspace-view orders-view">
            <div className="view-heading">
                <div>
                    <p className="section-kicker">ORDER OPERATIONS · O1 READ / O2 CROSS-BORDER WRITE</p>
                    <h1>订单中心</h1>
                    <p>订单主数据保持只读；跨境模板字段支持锁定前受控录入，不开放取消、发货或退款。</p>
                </div>
                <div className="order-heading-actions">
                    <span className={`data-source ${source}`}>● {source === "live" ? "订单数据库已连接" : "订单演示数据"}</span>
                    {canCreate && (
                        <button className="button primary" onClick={() => setShowCreate((visible) => !visible)} type="button">
                            {showCreate ? "收起建单" : "＋ 新增订单"}
                        </button>
                    )}
                </div>
            </div>

            {createdOrderNumber && (
                <div className="form-message success order-create-success" role="status">
                    订单 {createdOrderNumber} 已创建，状态为待支付。
                </div>
            )}

            {showCreate && csrfToken && (
                <ManualOrderCreate csrfToken={csrfToken} onCancel={() => setShowCreate(false)} onCreated={handleCreated} />
            )}

            <form className="order-filters card" onSubmit={submitSearch}>
                <label>
                    <span>订单 / 外部单号 / 商品</span>
                    <input value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder="输入关键词" />
                </label>
                <label>
                    <span>订单状态</span>
                    <select value={status} onChange={(event) => setStatus(event.target.value as OrderStatus | "")}>
                        <option value="">全部状态</option>
                        <option value="PENDING_PAYMENT">待支付</option>
                        <option value="PAID">已支付</option>
                        <option value="PARTIALLY_SHIPPED">部分发货</option>
                        <option value="COMPLETED">已完成</option>
                    </select>
                </label>
                <button className="button primary" disabled={isLoading} type="submit">
                    {isLoading ? "查询中…" : "查询订单"}
                </button>
                <span className="order-result-count">共 {result.total} 张订单</span>
            </form>

            <div className="order-layout">
                <div className="order-list card" aria-label="订单列表" role="region" tabIndex={0}>
                    <div className="order-table-head">
                        <span>订单</span>
                        <span>状态</span>
                        <span>金额</span>
                    </div>
                    {result.items.length === 0 ? (
                        <div className="order-empty">没有符合条件的订单</div>
                    ) : (
                        result.items.map((order) => (
                            <button
                                className={detail.id === order.id ? "order-row selected" : "order-row"}
                                key={order.id}
                                onClick={() => void openOrder(order.id)}
                                type="button"
                                aria-label={`查看订单 ${order.order_number}`}
                            >
                                <span className="order-identity">
                                    <strong>{order.order_number}</strong>
                                    <small>
                                        {order.channel} · {order.customer_name_masked} · {formatTime(order.placed_at)}
                                    </small>
                                </span>
                                <span>
                                    <i className={`order-status ${order.status.toLowerCase()}`}>{orderStatusLabel(order.status)}</i>
                                    <small>{orderStatusLabel(order.fulfillment_status)}</small>
                                </span>
                                <span className="order-amount">
                                    <strong>{formatMoney(order.total_minor, order.currency)}</strong>
                                    <small>{order.item_count} 件商品</small>
                                </span>
                            </button>
                        ))
                    )}
                </div>

                <aside className="order-detail card" aria-label="订单详情">
                    <div className="order-detail-head">
                        <div>
                            <span>订单详情</span>
                            <h2>{detail.order_number}</h2>
                        </div>
                        <i className={`order-status ${detail.status.toLowerCase()}`}>{orderStatusLabel(detail.status)}</i>
                    </div>
                    <dl className="order-summary-grid">
                        <div>
                            <dt>客户</dt>
                            <dd>{detail.customer_name_masked}</dd>
                        </div>
                        <div>
                            <dt>电话</dt>
                            <dd>{detail.customer_phone_masked}</dd>
                        </div>
                        <div className="wide">
                            <dt>收货地址</dt>
                            <dd>{detail.shipping_address_masked}</dd>
                        </div>
                        <div>
                            <dt>支付</dt>
                            <dd>{orderStatusLabel(detail.payment_status)}</dd>
                        </div>
                        <div>
                            <dt>履约</dt>
                            <dd>{orderStatusLabel(detail.fulfillment_status)}</dd>
                        </div>
                    </dl>

                    <div className="order-section-title">
                        <h3>商品明细</h3>
                        <span>{detail.items.reduce((sum, item) => sum + item.quantity, 0)} 件</span>
                    </div>
                    <div className="order-items">
                        {detail.items.map((item) => (
                            <div key={item.id}>
                                <span className="product-mark">SKU</span>
                                <span>
                                    <strong>{item.product_name}</strong>
                                    <small>
                                        {item.variant} · {item.sku} · ×{item.quantity}
                                    </small>
                                </span>
                                <b>{formatMoney(item.line_total_minor, detail.currency)}</b>
                            </div>
                        ))}
                    </div>

                    <dl className="amount-breakdown">
                        <div>
                            <dt>商品小计</dt>
                            <dd>{formatMoney(detail.subtotal_minor, detail.currency)}</dd>
                        </div>
                        <div>
                            <dt>优惠</dt>
                            <dd>-{formatMoney(detail.discount_minor, detail.currency)}</dd>
                        </div>
                        <div className="total">
                            <dt>订单总额</dt>
                            <dd>{formatMoney(detail.total_minor, detail.currency)}</dd>
                        </div>
                    </dl>

                    {source === "live" ? (
                        <CrossBorderPanel csrfToken={csrfToken} key={detail.id} orderId={detail.id} roles={roles} />
                    ) : (
                        <section className="cross-border-panel" aria-label="跨境贸易信息">
                            <div className="cross-border-error">
                                <strong>演示数据不提供跨境写入</strong>
                                <span>连接本地订单数据库后，才能读取模板并进行受控手工录入。</span>
                            </div>
                        </section>
                    )}

                    <div className="order-section-title">
                        <h3>状态轨迹</h3>
                        <span>版本 {detail.version}</span>
                    </div>
                    <div className="order-events">
                        {detail.events.map((event) => (
                            <div key={event.id}>
                                <i />
                                <span>
                                    <strong>{orderStatusLabel(event.to_status)}</strong>
                                    <small>
                                        {event.message} · {event.actor_label} · {formatTime(event.occurred_at)}
                                    </small>
                                </span>
                            </div>
                        ))}
                    </div>
                </aside>
            </div>
        </section>
    );
}
